import { errorCategories, errorToCodeException } from '../../../../errors';
import { isUsingAlchemyBundler } from '../../../../utils/isUsingAlchemyBundler';
import { selectUserOpsByAddress } from '../../../../store/userOpsStore';
import { NON_SPONSORED_FUNCTION_HASHES } from '../../../../constants';
import { decodeCallData } from '../../../../utils/decodeCallData';
import { paymasterDataSchema, paymasterErrorSchema, } from '../types';
import { getPaymasterModifiedGas } from './getPaymasterModifiedGas';
export const sponsorGas = async (args) => {
    const { paymasterProxyAuthSecret, paymasterProxyUrl, bundlerUrl, usePimlicoPaymaster, rpcRequest, accessToken, enableUsdc, } = args;
    try {
        const { paymasterData, shouldPayWithUSDC, error: paymasterError, } = await getSponsorshipPaymasterData({
            rpcRequest,
            paymasterProxyAuthSecret,
            accessToken,
            paymasterProxyUrl,
            usePimlicoPaymaster,
            bundlerUrl,
            enableUsdc,
        });
        // PRE USDC flow
        // return paymasterData if it exists - sponsor op
        // if !paymasterData, but error, throw
        // if !paymasterData and no error, return undefined - process op as normal
        if (!enableUsdc) {
            if (paymasterData) {
                return {
                    gas: getPaymasterModifiedGas({ paymasterData }),
                };
            }
            else if (paymasterError) {
                throw paymasterError;
            }
            else {
                return {
                    gas: undefined,
                };
            }
        }
        // POST USDC flow
        // return paymasterData if it exists - sponsor op
        // if !paymasterData, but error, throw
        // if !paymasterData and no error, return shouldPayWithUSDC: true
        else {
            if (paymasterData) {
                return {
                    gas: getPaymasterModifiedGas({ paymasterData }),
                    shouldPayWithUSDC: false,
                };
            }
            else if (shouldPayWithUSDC) {
                return {
                    gas: undefined,
                    shouldPayWithUSDC: true,
                };
            }
            else if (paymasterError) {
                // interrupt the rest of the flow if the paymaster errors for another reason
                throw paymasterError;
            }
            else {
                console.warn('[sponsorGas] Unexpected state: no paymaster data and no error');
                return {
                    gas: undefined,
                    shouldPayWithUSDC: false,
                };
            }
        }
    }
    catch (error) {
        console.error('[sponsorGas] error', error);
        throw error;
    }
};
async function getSponsorshipPaymasterData(args) {
    const { rpcRequest, paymasterProxyAuthSecret, accessToken, paymasterProxyUrl, usePimlicoPaymaster, bundlerUrl, enableUsdc, } = args;
    if (!allowSponsorship(rpcRequest, enableUsdc)) {
        // if usdc flag is off, returning undefined for paymaster and error means the op will continue as normal - backwards compatible
        // if usdc flag is on, the op will be processed with USDC
        return {
            paymasterData: undefined,
            shouldPayWithUSDC: enableUsdc,
            error: undefined,
        };
    }
    let sponsorUserOpUrl = `${paymasterProxyUrl}/api/sponsor-userop`;
    if (usePimlicoPaymaster) {
        sponsorUserOpUrl = `${paymasterProxyUrl}/api/sponsor-userop/pimlico`;
    }
    else if (isUsingAlchemyBundler(bundlerUrl)) {
        sponsorUserOpUrl = `${paymasterProxyUrl}/api/sponsor-userop/alchemy`;
    }
    const response = await fetch(sponsorUserOpUrl, {
        method: 'POST',
        body: JSON.stringify({
            data: rpcRequest,
        }),
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${paymasterProxyAuthSecret}`,
            'X-PM-Token': accessToken ?? '',
        },
    });
    const json = await response.json();
    if (!response.ok) {
        const errorParseResult = paymasterErrorSchema.safeParse(json);
        let errorCategory = errorCategories.userop_pm_error_code;
        // if this is a sponsorship limit error, we should pay with USDC
        if (errorParseResult.success) {
            return {
                paymasterData: undefined,
                shouldPayWithUSDC: true,
                error: new Error(`[getSponsorshipPaymasterData] Error getting gas policy response:: ${response.status} ${response.statusText} ${JSON.stringify(json)}`),
            };
        }
        if (!errorParseResult.success) {
            console.log('[getSponsorshipPaymasterData] errorParseResult.error, error may be missing in PaymasterErrorCode');
            errorCategory = errorCategories.userop_non_sponsored;
        }
        const error = new Error(`[getSponsorshipPaymasterData] Error getting paymaster proxy response:: ${response.status} ${response.statusText} ${JSON.stringify(json)}`);
        return {
            paymasterData: undefined,
            shouldPayWithUSDC: false,
            error: errorToCodeException(error, errorCategory),
        };
    }
    const parseResult = paymasterDataSchema.safeParse(json);
    if (!parseResult.success) {
        return {
            paymasterData: undefined,
            shouldPayWithUSDC: false,
            error: new Error(`[getSponsorshipPaymasterData] Error parsing PaymasterProxyResponse:: ${JSON.stringify(parseResult.error)}`),
        };
    }
    const parsedData = parseResult.data.data;
    return {
        paymasterData: parsedData,
        shouldPayWithUSDC: false,
        error: undefined,
    };
}
function allowSponsorship(userOp, enableUsdc) {
    const { current } = selectUserOpsByAddress(userOp.sender);
    const { functionHashForPaymasterProxy } = current;
    if (!enableUsdc) {
        // once we have USDC, we will sponsor ops with values
        const value = userOp.callData
            ? decodeCallData({
                callData: userOp.callData,
                functionHash: functionHashForPaymasterProxy,
            })?.value
            : undefined;
        if (value !== undefined && (value < 0n || value > 0n)) {
            return false;
        }
        // and we'll also sponsor "unsponsored" ops
        if (functionHashForPaymasterProxy &&
            NON_SPONSORED_FUNCTION_HASHES.includes(functionHashForPaymasterProxy)) {
            return false;
        }
    }
    return true;
}
